import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { SP, FS, PANEL_RADIUS } from '../../theme/tokens';
import { istChunkFehler } from '../../utils/lazyMitNeuladen';
import { meldeFehler } from '../../utils/fehlerbericht';

/**
 * ErrorBoundary — the net under a render error, and the one that names its class.
 *
 * @description
 *   Catches a render error in the tree below it and shows a card instead of an
 *   empty white screen. Since the routes are lazy (App.js) it answers TWO
 *   failures, and telling them apart is the point: a chunk that no longer
 *   resolves is a stale tab after a deploy and a reload fixes it; a genuine
 *   render error is not, and a reload there is a dead end that repeats itself.
 *   Before 2026-09-22 both looked identical and the operator could not choose.
 *
 *   🔴 It is mounted in THREE places, not one — `App.js` (inside `<Router>`,
 *   outside `<Routes>`), `SidebarAndHeaderBar` (around the `<Outlet/>`, so the
 *   frame survives) and `ProductDetail`. A claim about "the one consumer" ages
 *   the moment a fourth arrives; the count is deliberately not written down here.
 *
 * @useWhen
 *   - Around routed content, so a page crash does not take the frame with it
 *   - Around any subtree whose crash would otherwise blank the whole app
 *
 * @dontUseFor
 *   - Data-loading failures — those are the page's own error state, not a crash
 *   - Expected failures (a 404, a validation error) — those are states, not bugs
 *
 * @related
 *   - RouteFallback — the other half of the pair: this covers "failed", that
 *     covers "still loading"
 *   - utils/lazyMitNeuladen — the routes' self-healing: a stale chunk reloads
 *     the page once, so this card usually appears only when that reload did
 *     not happen or the error returns inside the window
 *
 * @reuseHints
 *   - Any future lazy boundary wants both halves of the pair
 *
 * @props
 *   children  — the subtree to guard.
 *   resetKey  — when it changes, a caught error is cleared and the children
 *               render again. Pass the pathname around routed content: without
 *               it the card stays up on every later route, with the previous
 *               page's error in its details (Regel-16-Gate, 2026-10-03). Not a
 *               `key` — that would remount the page on every param change.
 *   homeHref  — where "Go to Home" points (default `/`). `null` drops the link
 *               and offers "Reload page" instead: for a boundary whose audience
 *               cannot reach `/` (the public vendor report goes to anonymous
 *               vendors, and `/` sends them to the login) or whose crash `/`
 *               would only repeat (the app frame itself). The link is also
 *               dropped when the crash happened ON `homeHref`: it would only
 *               load the same crash again. Compared is the PATH only
 *               (`window.location.pathname`): pass a plain path, no query, no
 *               hash — `/x?y=1` would never match, and the guard would be off.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    // W5: der Absturz geht an den Server. Die KLASSE wird hier entschieden, nicht
    // in der Auswertung: ein Chunk-Fehler ist ein veralteter Tab, ein Render-
    // Fehler ein Defekt (Wiedervorlage W7).
    meldeFehler({
      art: istChunkFehler(error) ? 'chunk' : 'boundary',
      fehler: error,
      komponentenStapel: info?.componentStack,
    });
  }

  render() {
    const { error } = this.state;
    if (error) {
      // 🔴 Zwei Fehlerklassen, zwei Antworten. Seit die Routen lazy sind, ist
      // „ein Chunk kam nicht" ein eigener Fall: ein ueber ein Deployment offen
      // gebliebener Tab holt einen Hash, den es nicht mehr gibt. Ein Reload
      // behebt das — bei einem echten Render-Fehler ist er eine Sackgasse.
      // Vorher sahen beide gleich aus und der Bediener konnte sie nicht trennen.
      // Die Erkennung sitzt seit 2026-10-10 in `utils/lazyMitNeuladen.js`: die
      // ROUTEN umhuellen ihre `import()` damit und laden bei einem Chunk-Fehler
      // EINMAL selbst neu. Diese Karte erscheint also nur noch, wenn das
      // Neuladen ausblieb oder der Fehler innerhalb des Schleifenfensters erneut
      // auftrat.
      const msg = String(error?.message || error || '');
      const isChunk = istChunkFehler(error);
      const ziel = this.props.homeHref === undefined ? '/' : this.props.homeHref;
      // Abgestuerzt AUF dem Ziel: der Link luede nur denselben Absturz neu
      // (Regel-15-Gate O-8, 2026-10-03). Dann gilt es als kein Ausgang.
      const homeHref = ziel && window.location.pathname !== ziel ? ziel : null;
      // Ohne Startseiten-Ausgang bleibt fuer einen Render-Fehler nur das Neuladen.
      const offerReload = isChunk || !homeHref;
      let body;
      if (isChunk) {
        body = 'The app was updated or the connection dropped while this tab was open. Reloading usually fixes it.';
      } else if (homeHref) {
        body = 'Reloading would most likely show the same error. Go to home, or pick another page from the menu.';
      } else {
        // Im Rahmen (Vorgabe oder gesetzter `homeHref`) steht das Menue daneben,
        // und `resetKey` macht es zum echten Ausgang. `null` heisst: kein Rahmen.
        body = this.props.homeHref === null
          ? 'Reloading may help. If the error comes back, please try again later.'
          : 'Reloading may help, or pick another page from the menu.';
      }
      return (
        <Box data-testid="error-boundary" role="alert" sx={{ p: SP.card, textAlign: 'center', mt: SP.card }}>
          <Typography variant="h6" color="error" sx={{ mb: SP.tight }}>
            {isChunk ? 'This page could not be loaded' : 'This page crashed'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: SP.card }}>
            {body}
          </Typography>
          {/* 🔴 ZWEI Ausgaenge, nicht einer. „Reload page" ist fuer den Chunk-Fall
              richtig und fuer einen Render-Fehler eine Sackgasse: derselbe Baum,
              derselbe Wurf. Der Weg zurueck geht ueber eine ANDERE Route, und
              `component="a"` laedt sie frisch statt sie durch den kaputten Baum
              zu routen. Ohne `homeHref` (aeussere Grenze) bleibt nur das Neuladen. */}
          <Box sx={{ display: 'flex', gap: SP.tight, justifyContent: 'center', mb: SP.card, flexWrap: 'wrap' }}>
            {homeHref && (
              <Button component="a" href={homeHref} variant={offerReload ? 'outlined' : 'contained'}>
                Go to home
              </Button>
            )}
            {offerReload && (
              <Button variant="contained" onClick={() => window.location.reload()}>
                Reload page
              </Button>
            )}
          </Box>
          {/* 🔴 Die technische Meldung ist BELEGT, aber nicht die erste Zeile.
              Vorher stand sie roh ueber dem einzigen Knopf — fuer den Bediener
              das Erste, was er las, und fuer den Chunk-Fall ohnehin Rauschen. */}
          <Box component="details" sx={{ textAlign: 'left' }}>
            {/* Tippziel ≥ 44 px (Regel 22 §8); `summary` behaelt seine Liste-
                Darstellung, damit das Aufklapp-Dreieck bleibt. */}
            <Typography
              component="summary"
              variant="body2"
              color="text.secondary"
              sx={{ cursor: 'pointer', minHeight: 44, lineHeight: '44px' }}
            >
              Technical details
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              // `pre-wrap` bricht nur an Leerzeichen; eine Chunk-URL hat keine und
              // sprengte unter 600 px die Karte (Regel-16-Gate W6).
              sx={{ mt: SP.tight, fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                bgcolor: 'action.hover', p: SP.card, borderRadius: PANEL_RADIUS, fontSize: FS.sm }}
            >
              {msg}
            </Typography>
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}
